import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AlertTriangle, ArrowRight, CheckCircle, ClipboardCheck, Clock, Code2, FileEdit, Layers, RefreshCw, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { DashboardController } from '@/presentation/controllers/dashboard-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useDashboard } from '@/presentation/hooks/use-dashboard.js';
import { BRAND_NAVY, BRAND_ORANGE, BRAND_RED } from '@/shared/constants/brand.js';
import { ROUTES, toDevelopmentDetail } from '@/shared/constants/routes.js';
import { formatRelative } from '@/shared/utils/format-date.js';

const BRAND_BLUE = '#5E4AE3';
const BRAND_TEAL = '#4AE3D6';

const STATUS_BADGE_STYLE: Record<string, { bgcolor: string; color: string }> = {
    EF_EM_ELABORACAO: { bgcolor: '#FEF3C7', color: '#92400E' },
    EF_EM_VALIDACAO_IA: { bgcolor: '#DBEAFE', color: '#1E40AF' },
    EF_REJEITADA_IA: { bgcolor: '#FEE2E2', color: '#991B1B' },
    EF_VALIDADA_IA: { bgcolor: '#D1FAE5', color: '#166534' },
    AGUARDANDO_ATRIBUICAO_TECNICA: { bgcolor: '#FFEDD5', color: '#9A3412' },
    EM_ANALISE_TECNICA: { bgcolor: '#F3E8FF', color: '#6B21A8' },
    AGUARDANDO_APROVACAO_ESTIMATIVA: { bgcolor: '#FFEDD5', color: '#9A3412' },
    ESTIMATIVA_EM_REVISAO: { bgcolor: '#FEF3C7', color: '#92400E' },
    AGUARDANDO_INICIO: { bgcolor: '#F3F4F6', color: '#374151' },
    EM_DESENVOLVIMENTO: { bgcolor: '#DBEAFE', color: '#1E40AF' },
    AGUARDANDO_REVISAO_DOCUMENTACAO: { bgcolor: '#FFEDD5', color: '#9A3412' },
    EM_VALIDACAO_CONSULTOR: { bgcolor: '#F3E8FF', color: '#6B21A8' },
    EM_VALIDACAO_USUARIO_CHAVE: { bgcolor: '#E0E7FF', color: '#3730A3' },
    COM_SOLICITACOES_PENDENTES: { bgcolor: '#FEE2E2', color: '#991B1B' },
    ENCERRADO: { bgcolor: '#D1FAE5', color: '#166534' },
    CANCELADO: { bgcolor: '#F3F4F6', color: '#6B7280' }
};

const STATUS_BAR_COLOR: Record<string, string> = {
    EF_EM_ELABORACAO: '#FBBF24',
    EF_EM_VALIDACAO_IA: '#60A5FA',
    EF_REJEITADA_IA: '#F87171',
    EF_VALIDADA_IA: '#34D399',
    AGUARDANDO_ATRIBUICAO_TECNICA: '#FB923C',
    EM_ANALISE_TECNICA: '#A78BFA',
    AGUARDANDO_APROVACAO_ESTIMATIVA: '#FDBA74',
    ESTIMATIVA_EM_REVISAO: '#FCD34D',
    AGUARDANDO_INICIO: '#D1D5DB',
    EM_DESENVOLVIMENTO: '#3B82F6',
    AGUARDANDO_REVISAO_DOCUMENTACAO: '#F97316',
    EM_VALIDACAO_CONSULTOR: '#8B5CF6',
    EM_VALIDACAO_USUARIO_CHAVE: '#818CF8',
    COM_SOLICITACOES_PENDENTES: '#EF4444',
    ENCERRADO: '#22C55E',
    CANCELADO: '#9CA3AF'
};

interface Props {
    controller: DashboardController;
}

export function DashboardPage({ controller }: Props) {
    const { translate, useAuthStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const { data, isLoading, error, reload } = useDashboard(controller);

    if (isLoading && !data) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    const hour = new Date().getHours();
    const greeting = hour < 12 ? translate('greeting.morning') : hour < 18 ? translate('greeting.afternoon') : translate('greeting.evening');
    const kpis = data?.kpis;
    const maxCount = data ? Math.max(...data.statusDistribution.map((s) => s.count), 1) : 1;

    const kpiCards = [
        { label: translate('dashboard.kpiTotal'), value: kpis?.total ?? 0, icon: Layers, color: BRAND_NAVY, bg: `${BRAND_NAVY}1A`, showPct: false },
        { label: translate('dashboard.kpiElabFunc'), value: kpis?.elaboracaoFuncional ?? 0, icon: FileEdit, color: BRAND_ORANGE, bg: `${BRAND_ORANGE}1A`, showPct: true },
        { label: translate('dashboard.kpiAlocacaoFab'), value: kpis?.alocacaoFabrica ?? 0, icon: Users, color: BRAND_TEAL, bg: `${BRAND_TEAL}1A`, showPct: true },
        { label: translate('dashboard.kpiAprovEstim'), value: kpis?.aprovacaoEstimativa ?? 0, icon: Clock, color: BRAND_BLUE, bg: `${BRAND_BLUE}1A`, showPct: true },
        { label: translate('dashboard.kpiInDev'), value: kpis?.emDesenvolvimento ?? 0, icon: Code2, color: BRAND_NAVY, bg: `${BRAND_NAVY}1A`, showPct: true },
        { label: translate('dashboard.kpiEmCorrecao'), value: kpis?.emCorrecao ?? 0, icon: AlertTriangle, color: BRAND_RED, bg: `${BRAND_RED}1A`, showPct: true },
        { label: translate('dashboard.kpiValidFunc'), value: kpis?.validacaoFuncional ?? 0, icon: ClipboardCheck, color: BRAND_ORANGE, bg: `${BRAND_ORANGE}1A`, showPct: true },
        { label: translate('dashboard.kpiEncerrados'), value: kpis?.encerrados ?? 0, icon: CheckCircle, color: BRAND_TEAL, bg: `${BRAND_TEAL}1A`, showPct: true }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {greeting}, {user?.name?.split(' ')[0]}!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {user ? translate(`profiles.${user.profile}`) : ''} — {translate('greeting.overview')}
                    </Typography>
                </Box>
                <Button
                    startIcon={<RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />}
                    onClick={reload}
                    disabled={isLoading}
                    size="small"
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    {translate('common.refresh')}
                </Button>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {kpiCards.map((card) => {
                    const Icon = card.icon;
                    return (
                        <Grid key={card.label} size={{ xs: 6, sm: 3 }}>
                            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 3, '&:last-of-type': { pb: 3 } }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: card.bg }}>
                                        <Icon size={20} color={card.color} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700} color="text.primary">
                                            {isLoading ? '—' : card.value}
                                            {card.showPct && (
                                                <Typography component="span" variant="body2" fontWeight={500} sx={{ ml: 0.75, color: 'text.disabled' }}>
                                                    ({isLoading ? '—' : (kpis?.percentageOf(card.value) ?? '0%')})
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', lineHeight: 1.3 }}>
                                            {card.label}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
                        <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                    {translate('dashboard.recentActivity')}
                                </Typography>
                                <Link to={ROUTES.DEVELOPMENTS} style={{ textDecoration: 'none' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: BRAND_ORANGE, display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}
                                    >
                                        {translate('dashboard.viewAll')} <ArrowRight size={12} />
                                    </Typography>
                                </Link>
                            </Stack>
                            {!data || data.recentDevelopments.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 3,
                                            bgcolor: 'grey.100',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 1.5
                                        }}
                                    >
                                        <Code2 size={22} style={{ color: '#9CA3AF' }} />
                                    </Box>
                                    <Typography variant="body2" fontWeight={500} color="text.secondary">
                                        {translate('dashboard.noDevsFound')}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                                        {translate('dashboard.noDevsMsg')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ mx: -3 }}>
                                    {data.recentDevelopments.map((dev, idx) => {
                                        const badgeStyle = STATUS_BADGE_STYLE[dev.status] ?? { bgcolor: '#F3F4F6', color: '#6B7280' };
                                        return (
                                            <Link key={dev.id} to={toDevelopmentDetail(dev.id)} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        px: 3,
                                                        py: 1.5,
                                                        borderTop: idx > 0 ? '1px solid #F3F4F6' : 'none',
                                                        '&:hover': { bgcolor: 'grey.50' }
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', flexShrink: 0 }}>
                                                                {dev.displayId}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: 5,
                                                                    fontSize: 11,
                                                                    fontWeight: 500,
                                                                    flexShrink: 0,
                                                                    ...badgeStyle
                                                                }}
                                                            >
                                                                {translate(`statusLabels.${dev.status}`)}
                                                            </Box>
                                                        </Stack>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                            sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                        >
                                                            {dev.title}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                                                            {dev.clientName} · {dev.projectName}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, mt: 0.5, whiteSpace: 'nowrap' }}>
                                                        {formatRelative(dev.updatedAt, translate)}
                                                    </Typography>
                                                </Box>
                                            </Link>
                                        );
                                    })}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
                        <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                            <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                                {translate('dashboard.statusDistribution')}
                            </Typography>
                            {!data || data.statusDistribution.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
                                    <Typography variant="body2" color="text.disabled">
                                        {translate('dashboard.noDataAvailable')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {data.statusDistribution.map((item) => {
                                        const barColor = STATUS_BAR_COLOR[item.status] ?? '#D1D5DB';
                                        return (
                                            <Box key={item.status}>
                                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', pr: 1 }}>
                                                        {translate(`statusLabels.${item.status}`)}
                                                    </Typography>
                                                    <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0 }}>
                                                        {item.count}
                                                    </Typography>
                                                </Stack>
                                                <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 3, overflow: 'hidden' }}>
                                                    <Box
                                                        sx={{
                                                            height: '100%',
                                                            borderRadius: 3,
                                                            bgcolor: barColor,
                                                            width: `${(item.count / maxCount) * 100}%`,
                                                            transition: 'width 0.5s ease'
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
